import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { useLazyExportUsersQuery } from "../../../RTKQuery/CsvDataService/csvDataApi";

const ExportModal = ({
  open,
  onClose,
  total,
  filters,
  sorting,
  filterType,
  campaignId,
  suppressedUserIds,
  onExportComplete,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [exportType, setExportType] = useState("all");
  const [batchSize, setBatchSize] = useState(1);
  const [userCount, setUserCount] = useState(0);

  const [exportUsers, { isLoading: exporting }] = useLazyExportUsersQuery();

  const handleExport = async () => {
    try {
      const batchCount = exportType === "batch" ? batchSize : 1;
      const userCountValue = exportType === "specific" ? userCount : total;
      const dataExportType =
        activeTab === 0 ? "sendgrid" : activeTab === 1 ? "apollo" : "raw";

      await exportUsers({
        filters,
        sorting,
        batchCount,
        userCount: userCountValue,
        total,
        exportType: dataExportType,
        filterType: filterType,
        campaignId: campaignId,
        suppressedUserIds: suppressedUserIds,
      }).unwrap();

      // Call onExportComplete callback if provided (e.g., to refetch version history)
      if (onExportComplete) {
        onExportComplete();
      }

      handleClose();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleClose = () => {
    setActiveTab(0);
    setExportType("all");
    setBatchSize(1);
    setUserCount(0);
    onClose();
  };

  const exportOptions = [
    {
      value: "all",
      label: "Export All Users",
      description: `Export all ${total} users`,
      count: total,
    },
    {
      value: "batch",
      label: "Export in Multiple CSVs",
      description: "Split users into multiple CSV files for easier processing",
    },
    {
      value: "specific",
      label: "Export Sorted Users",
      description: "Export a specific number of users based on current sorting",
      disabled: sorting.length < 1,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "1rem",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Export Users Data
        </Typography>
        <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
          Choose between SendGrid, Apollo format (Name, Email) or complete data
          export
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="SendGrid Export" />
          <Tab label="Apollo Export" />
          <Tab label="Raw Data Export" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <RadioGroup
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            sx={{ mt: 1 }}
          >
            {exportOptions.map((option) => (
              <Box
                key={option.value}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "1rem",
                  marginBottom: "0.5rem",
                  backgroundColor:
                    exportType === option.value ? "#f5f5f5" : "transparent",
                  transition: "all 0.2s ease",
                  cursor: option.disabled ? "not-allowed" : "pointer",
                  opacity: option.disabled ? 0.6 : 1,
                  "&:hover": {
                    backgroundColor: option.disabled ? "#f5f5f5" : "#f9f9f9",
                  },
                }}
                onClick={() => !option.disabled && setExportType(option.value)}
              >
                <FormControlLabel
                  value={option.value}
                  control={<Radio disabled={option.disabled} />}
                  label={
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "medium" }}
                      >
                        {option.label}
                        {option.count && (
                          <Typography
                            component="span"
                            sx={{ color: "#8ABA5C", marginLeft: "0.5rem" }}
                          >
                            ({option.count})
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "gray", mt: 0.5 }}
                      >
                        {option.description}
                        {activeTab === 0 && (
                          <span> - SendGrid format (Name, Email only)</span>
                        )}
                        {activeTab === 1 && (
                          <span> - Apollo format (Name, Email only)</span>
                        )}
                        {activeTab === 2 && (
                          <span>
                            {" "}
                            - Complete data (Name, Email, Campaigns, Delivered,
                            Bounced, Opens, Clicks, Open Rate, etc.)
                          </span>
                        )}
                      </Typography>

                      {/* Input fields inside the option block */}
                      {exportType === option.value &&
                        option.value === "batch" && (
                          <Box sx={{ mt: 2, ml: 2 }}>
                            <TextField
                              fullWidth
                              label="Number of files"
                              type="number"
                              size="small"
                              value={batchSize}
                              onChange={(e) => setBatchSize(e.target.value)}
                              sx={{ maxWidth: 200 }}
                            />
                          </Box>
                        )}
                      {exportType === option.value &&
                        option.value === "specific" && (
                          <Box sx={{ mt: 2, ml: 2 }}>
                            <TextField
                              fullWidth
                              label="Number of Users"
                              type="number"
                              size="small"
                              value={userCount}
                              onChange={(e) => setUserCount(e.target.value)}
                              sx={{ maxWidth: 200 }}
                            />
                          </Box>
                        )}
                    </Box>
                  }
                  sx={{ margin: 0, width: "100%" }}
                  disabled={option.disabled}
                />
              </Box>
            ))}
          </RadioGroup>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button
          onClick={handleClose}
          disabled={exporting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={
            (exportType === "batch" && !batchSize) ||
            (exportType === "specific" && !userCount) ||
            exporting
          }
          startIcon={exporting ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: "none" }}
        >
          {exporting
            ? "Exporting..."
            : `Export ${
                activeTab === 0
                  ? "for SendGrid"
                  : activeTab === 1
                  ? "for Apollo"
                  : "Raw Data"
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportModal;
